import { BudgetCard } from './budget-card';
import { 
  DollarSign, 
  TrendingDown, 
  Percent, 
  Clock, 
  Activity 
} from 'lucide-react';
import { 
  formatCurrency, 
  formatPercent, 
  formatHours,
  formatVariance 
} from '@/lib/budget-utils';
import type { BudgetSummary } from '@/types/budget.types';

interface BudgetSummaryCardsProps {
  summary: BudgetSummary;
}

export function BudgetSummaryCards({ summary }: BudgetSummaryCardsProps) {
  const { revenue, costs, margin, hours, health } = summary;

  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
      {/* Revenue Card */}
      <BudgetCard
        title="Revenue"
        primaryValue={revenue.budget}
        secondaryValue={`Actual: ${formatCurrency(revenue.actual)}`}
        progressPercent={revenue.progressPercent}
        status={revenue.status}
        trend={revenue.trend === 'stable' ? 'neutral' : revenue.trend}
        trendValue={revenue.trendValue}
        tertiaryValue={revenue.remaining ? `${formatCurrency(revenue.remaining)} remaining` : undefined}
        icon={<DollarSign className="h-5 w-5" />}
        tooltip="Total sales budget vs actual revenue recognized"
      />

      {/* Costs Card */}
      <BudgetCard
        title="Costs"
        primaryValue={costs.budget}
        secondaryValue={`Actual: ${formatCurrency(costs.actual)}`}
        progressPercent={costs.progressPercent}
        status={costs.status}
        trend={costs.trend === 'stable' ? 'neutral' : costs.trend}
        trendValue={costs.trendValue}
        tertiaryValue={(costs.committed ?? 0) > 0 ? `${formatCurrency(costs.committed ?? 0)} committed` : `${formatCurrency(costs.available ?? 0)} available`}
        icon={<TrendingDown className="h-5 w-5" />}
        tooltip="Total cost budget including actual and committed costs"
      />

      {/* Margin Card */}
      <BudgetCard
        title="Margin"
        primaryValue={formatPercent(margin.budgetedMargin)}
        secondaryValue={`Actual: ${formatPercent(margin.actualMargin)}`}
        progressPercent={undefined}
        status={margin.status}
        trend={margin.trend === 'stable' ? 'neutral' : margin.trend}
        trendValue={formatVariance(margin.variance, 'percent')}
        icon={<Percent className="h-5 w-5" />}
        tooltip="Gross margin percentage (Revenue - Costs) / Revenue"
      />

      {/* Hours Card */}
      <BudgetCard
        title="Hours"
        primaryValue={formatHours(hours.budget)}
        secondaryValue={`Used: ${formatHours(hours.actual)}`}
        progressPercent={hours.progressPercent}
        status={hours.status}
        trend={hours.trend === 'stable' ? 'neutral' : hours.trend}
        trendValue={hours.trendValue}
        tertiaryValue={`${formatHours(hours.remaining)} remaining`}
        icon={<Clock className="h-5 w-5" />}
        tooltip="Total hours budget vs actual hours logged"
      />

      {/* Health Card */}
      <BudgetCard
        title="Budget Health"
        primaryValue={health.score.toUpperCase()}
        secondaryValue={health.statusText}
        progressPercent={undefined}
        status={health.score}
        trend={undefined}
        trendValue={undefined}
        icon={<Activity className="h-5 w-5" />}
        tooltip={health.factors.join(', ')}
      />
    </div>
  );
}
